import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client.js';
import { NachreichungUpload } from '../components/NachreichungUpload.js';
import type { MeResponse, QuestionnaireResponse, UploadMeta } from '@gql/shared-types';

const BOOKINGS_URL = (import.meta.env.VITE_BOOKINGS_URL as string | undefined) ?? '';
const HAIDAR_TAX_QUESTIONNAIRE_SLUG = 'haidar-tax-2021-2022';

export function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [nachreichungen, setNachreichungen] = useState<UploadMeta[]>([]);
  const [nachreichungenLoaded, setNachreichungenLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.me();
        setMe(meRes);
        if (meRes.hasQuestionnaire) {
          try {
            setQuestionnaire(await api.getQuestionnaire());
          } catch (err) {
            console.error('failed to load assigned questionnaire', err);
          }
        }
        if (meRes.canUploadNachreichung) {
          try {
            const nr = await api.getNachreichungen();
            setNachreichungen(nr.uploads);
          } catch (err) {
            console.error('failed to load nachreichungen', err);
          } finally {
            setNachreichungenLoaded(true);
          }
        } else {
          setNachreichungenLoaded(true);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onLogout = async () => {
    try { await api.logout(); } catch {}
    navigate('/login', { replace: true });
  };

  if (loading) return <div className="card"><h2 className="card-title">{t('dashboard.loading')}</h2></div>;
  if (!me) return <div className="card"><h2 className="card-title">{t('dashboard.notLoggedIn')}</h2></div>;

  const hasHaidarTaxForm = questionnaire?.slug === HAIDAR_TAX_QUESTIONNAIRE_SLUG;
  // Andere fallspezifische Fragebögen behalten den bisherigen Intake-Weg.
  // Der Haidar-Steuerfragebogen erscheint zusätzlich als eigener, nur zugewiesenen
  // Nutzern sichtbarer Portalpunkt.
  const intakeTarget = me.hasQuestionnaire && !hasHaidarTaxForm ? '/intake' : '/onboarding';

  return (
    <div className="dashboard">
      <div className="card">
        <h2 className="card-title">{t('dashboard.welcome', { name: me.vorname })}</h2>
        <p className="card-subtitle">
          {t(hasHaidarTaxForm ? 'dashboard.subtitleWithTax' : 'dashboard.subtitle')}
        </p>

        <dl className="kv">
          <dt>{t('dashboard.name')}</dt>
          <dd>{me.vorname} {me.nachname}</dd>
          <dt>{t('dashboard.email')}</dt>
          <dd>{me.email}</dd>
          <dt>{t('dashboard.verifiedSince')}</dt>
          <dd>{me.verifiedAt ? new Date(me.verifiedAt).toLocaleString() : '—'}</dd>
        </dl>

        <button className="button button-secondary" onClick={onLogout}>{t('dashboard.logout')}</button>
      </div>

      <div className="card">
        <h3 className="card-h">{t('dashboard.requestH')}</h3>
        {me.submitted ? (
          <div className="alert alert-success">{t('dashboard.submitted')}</div>
        ) : me.hasDraft ? (
          <>
            <p className="card-subtitle">{t('dashboard.draftHint')}</p>
            <Link to={intakeTarget} className="button">{t('dashboard.continue')}</Link>
          </>
        ) : (
          <>
            <p className="card-subtitle">{t('dashboard.startHint')}</p>
            <Link to={intakeTarget} className="button">{t('dashboard.start')}</Link>
          </>
        )}
      </div>

      {hasHaidarTaxForm && (
        <div className="card">
          <h3 className="card-h">{t('dashboard.taxH')}</h3>
          {me.submitted ? (
            <div className="alert alert-success">{t('dashboard.taxSubmitted')}</div>
          ) : (
            <>
              <p className="card-subtitle">{t('dashboard.taxHint')}</p>
              <Link to="/intake" className="button">
                {t(me.hasDraft ? 'dashboard.taxContinue' : 'dashboard.taxStart')}
              </Link>
            </>
          )}
        </div>
      )}

      {me.canUploadNachreichung && nachreichungenLoaded && (
        <div className="card">
          <h3 className="card-h">{t('dashboard.nachreichungH')}</h3>
          <p className="card-subtitle">{t('dashboard.nachreichungHint')}</p>
          <NachreichungUpload
            initialUploads={nachreichungen}
            onUploaded={(newCount, total) => {
              setMe((prev) => prev ? { ...prev, nachreichungCount: total } : prev);
              console.log(`[dashboard] +${newCount} nachreichung(en), total=${total}`);
            }}
          />
        </div>
      )}

      <div className="card">
        <h3 className="card-h">{t('dashboard.terminH')}</h3>
        <p className="card-subtitle">{t('dashboard.terminHint')}</p>
        {BOOKINGS_URL ? (
          <>
            <a className="button" href={BOOKINGS_URL} target="_blank" rel="noopener noreferrer">
              {t('dashboard.bookButton')}
            </a>
            <details style={{ marginTop: 16 }}>
              <summary className="text-link">{t('dashboard.bookInline')}</summary>
              <iframe
                src={BOOKINGS_URL}
                title={t('dashboard.bookFrameTitle')}
                style={{ width: '100%', height: 720, border: 0, marginTop: 12, borderRadius: 6 }}
              />
            </details>
          </>
        ) : (
          <div className="alert alert-info">{t('dashboard.bookSoon')}</div>
        )}
      </div>
    </div>
  );
}
